import { Box, Card, CardContent, Button } from "@mui/material";
import { useState, ChangeEvent, useRef } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import * as XLSX from 'xlsx';
import { ApiResponse } from "../../../models/ApiResponse";
import api from "../../../services/ApiService";
import InfoIcon from '@mui/icons-material/Info';

const BulkCreateTooling = () =>{
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileData, setFileData] = useState<any[]>([]);
  const [columnData, setColumnData] = useState<any[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleFileChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    setSelectedFile(file);
  };

  const handleClear = () => {
    setColumnData([]);
    setFileData([]);
    setIsError(false);
    setSelectedFile(null);
    fileInputRef.current.value = "";
  };
  const fileInputRef = useRef(null);

  const handleBrowseClick = () => {
    setColumnData([]);
    setFileData([]);
    setIsError(false);
    fileInputRef.current.click();
  };

  const processErrorData = (errorResponse) => {
    const reader = new FileReader();
  
    reader.onload = (e) => {
      const result = e.target?.result;
  
      if (!(result instanceof ArrayBuffer)) {
        console.error("Invalid file format");
        return;
      }
  
      const data = new Uint8Array(result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
  
      // Parse to JSON with headers, fill empty cells with empty string
      let rowData: any = XLSX.utils.sheet_to_json(sheet, {
        defval: "",
        raw: false,
      });
  
      if (
        rowData.length &&
        Object.values(rowData[rowData.length - 1]).every((val) => val === "")
      ) {
        rowData = rowData.slice(0, -1);
      }
  
      // Match by index+1 or by PrjCode
      const errorRowData = rowData.filter(
        (item, index) => {
          if (errorResponse?.message.includes("Invalid Series Name")){
            return errorResponse?.errors?.includes(item?.["Series Name"]); 
          } else if (errorResponse?.message.includes("Attachment Name")){
            return errorResponse?.errors?.includes(item?.["Attachment Name"]); 
          } else{
            return errorResponse?.errors?.includes((index + 1).toString()); // Assuming errors can be row numbers
          }
        }
      );
  
      if (errorRowData?.length > 0) {
        const columns = Object.keys(errorRowData[0]).map((field) => ({
          field,
          headerName: field,
          width: 150,
        }));
  
        const convertedData = errorRowData.map((item, index) => ({
          ...item,
          id: `${item?.PrjCode}-${index + 1}`,
        }));
  
        setColumnData(columns);
        setFileData(convertedData);
      }
  
      fileInputRef.current.value = ""; // Reset file input
      setErrorMessage(errorResponse.message || "An error occurred");
      setSelectedFile(null);
      setIsError(true);
    };
  
    reader.readAsArrayBuffer(selectedFile);
  };

  const handleSubmit = async () => {
    const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
    const formData = new FormData();
    if (selectedFile.size < maxSizeInBytes) {
      formData.append("file", selectedFile); // Assuming 'file' is the key expected by the server
      api
        .post<ApiResponse<any>>("/Tooling/bulk-create", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          if (response.isSuccess) {
            toast.success(response.message);
            setSelectedFile(null);
            fileInputRef.current.value = "";
          }
        })
        .catch((error) => {
          if (error?.response?.data?.errors?.length > 0) {
            const errorResponse = error.response.data;
            processErrorData(errorResponse);
          }
          console.error("Exception from bulk create tooling file", error);
        });
    } else {
      toast.error("Upload file less than 5MB");
    }
  };

  const handleUpdateSampleDownload = () => {
    const demoXlsxUrl = "./sampleBulkCreateToolings.xlsx";
    const link = document.createElement("a");
    link.href = demoXlsxUrl;
    link.setAttribute("download", "sampleBulkCreateToolings.xlsx");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
      <Card className="bulk-common-upload">
        <CardContent>
          <div className="title">
            <p>Bulk Tooling Create </p>
            <div style={{ display: "flex", alignItems: "start", marginTop: "10px" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <InfoIcon sx={{ fontSize: 18, marginRight: "2px" }} />
                  <p style={{ fontSize: "12px", margin: "0" }}>Notes:</p>
                </div>
                <ul style={{ fontSize: "12px", marginTop: "4px", paddingLeft: "20px" }}>
                  <li>The Excel file must contain a worksheet named <b>"Toolings"</b>. Files with any other worksheet name will be rejected.</li>
                  <li>Please upload the attachments into&nbsp;
                    <a
                      href={process.env.REACT_APP_SHAREPOINT_INTAKE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontWeight: "bold", textTransform: "none" }}
                    >
                      this folder on SharePoint
                    </a>
                    &nbsp;before proceeding with the Bulk Create Tooling
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <Box className="container">
            <input
              className="file-input"
              type="file"
              id="fileInput"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".xls,.xlsx"
            />
            <div className="inputWrap">
              <input
                type="text"
                name="fileName"
                id="fileName"
                value={selectedFile ? selectedFile.name : ""}
                readOnly
              />
              
              <p>
                XLSX, XLS (max. 5MB).
                <span
                  className="download-sample"
                  onClick={handleUpdateSampleDownload}
                >
                  <strong>Download sample XLSX for bulk create</strong>
                </span>
              </p>
            </div>

            <Button
              onClick={handleBrowseClick}
              variant="contained" >
              Browse
            </Button>

            <Button
              onClick={handleSubmit}
              disabled={selectedFile ? false : true}
            >
              Submit
            </Button>
            <Button className="clear-button"
              onClick={handleClear} >
              Clear
            </Button>
          </Box>

          {isError ? (
            <>
              <div className="errors-container" >
                <p>{errorMessage}</p>
              </div>
              <Box className="bulk-update-grid-cell">
                {fileData.length > 0 && (
                  <DataGrid
                    rows={fileData}
                    columns={columnData}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                      toolbar: {
                        showQuickFilter: true,
                      },
                    }}
                    disableColumnFilter
                    disableColumnSelector
                    disableDensitySelector
                    disableRowSelectionOnClick={true}
                    getRowHeight={() => 'auto'}
                  />
                )}
              </Box>
            </>
          ) : (
            ""
          )}
        </CardContent>
      </Card>
  );
};

export default BulkCreateTooling;
